import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LeaveFilter, LeaveRequestFilter, LeaveState } from "./leave.types";
import { RootState } from "../../app/store";
import { leaveApi } from "./leave.api";
import { PagedSearchFilter } from "../../app/types";

const now = new Date();

const initialState: LeaveState = {
    employeeLeaves: {
        items: [],
        total: 0,
    },
    employeeLeavesFilter: {
        year: new Date().getFullYear(),
        searchTerm: "",
    },
    leaveRequestsFilter: {
        searchTerm: "",
    },
    myLeaveRequestsFilter: {
        searchTerm: "",
        from: `${now.getFullYear()}-01-01`,
        to: `${now.getFullYear()}-12-31`,
    },
    myLeaveApprovalsFilter: {
        searchTerm: "",
    },
};

export const fetchLeaveTypesListAsync = createAsyncThunk("leave/leaveTypes", async () => {
    const leaveTypes = await leaveApi.listLeaveTypes();
    return leaveTypes;
});

export const fetchEmployeeLeavesAsync = createAsyncThunk(
    "leave/employeeLeaves",
    async (filter: PagedSearchFilter<LeaveFilter>) => {
        const employeeLeaves = await leaveApi.searchEmployeeLeave(filter);
        return employeeLeaves;
    },
);

export const fetchLeaveTypeSelectListAsync = createAsyncThunk("leave/leaveTypesSelectList", async () => {
    const leaveTypes = await leaveApi.getLeaveTypesSelectList();
    return leaveTypes;
});

export const fetchLeaveRequestsAsync = createAsyncThunk(
    "leave/requests",
    async (filter: PagedSearchFilter<LeaveRequestFilter>) => {
        const leaveRequests = await leaveApi.getLeaveApprovals(filter);
        return leaveRequests;
    },
);

export const fetchMyLeaveRequestsAsync = createAsyncThunk(
    "leave/requests/currentuser",
    async (filter: PagedSearchFilter<LeaveRequestFilter>) => {
        const leaveRequests = await leaveApi.getCurrentUserLeaveRequests(filter);
        return leaveRequests;
    },
);

export const fetchMyLeaveApprovalsAsync = createAsyncThunk(
    "leave/approvals/currentuser",
    async (filter: PagedSearchFilter<LeaveRequestFilter>) => {
        const leaveRequests = await leaveApi.getCurrentUserLeaveApprovals(filter);
        return leaveRequests;
    },
);

export const leaveSlice = createSlice({
    name: "leave",
    initialState,
    reducers: {
        leaveFilterChange: (state, action) => {
            state.employeeLeavesFilter = { ...state.employeeLeavesFilter, ...action.payload };
        },
        leaveRequestsFilterChange: (state, action) => {
            state.leaveRequestsFilter = { ...state.leaveRequestsFilter, ...action.payload };
        },
        myLeaveRequestsFilterChange: (state, action) => {
            state.myLeaveRequestsFilter = { ...state.myLeaveRequestsFilter, ...action.payload };
        },
        myLeaveApprovalsFilterChange: (state, action) => {
            state.myLeaveApprovalsFilter = { ...state.myLeaveApprovalsFilter, ...action.payload };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLeaveTypesListAsync.fulfilled, (state, action) => {
                state.leaveTypes = action.payload;
            })
            .addCase(fetchEmployeeLeavesAsync.fulfilled, (state, action) => {
                state.employeeLeaves = action.payload;
                state.employeeLeavesSeachInprogress = false;
            })
            .addCase(fetchEmployeeLeavesAsync.pending, (state) => {
                state.employeeLeavesSeachInprogress = true;
            })
            .addCase(fetchEmployeeLeavesAsync.rejected, (state) => {
                state.employeeLeavesSeachInprogress = false;
            })
            .addCase(fetchLeaveTypeSelectListAsync.fulfilled, (state, action) => {
                state.leaveTypesSelectList = action.payload;
            })
            .addCase(fetchLeaveRequestsAsync.fulfilled, (state, action) => {
                state.leaveRequests = action.payload;
                state.leaveRequestsSearchInprogress = false;
            })
            .addCase(fetchLeaveRequestsAsync.pending, (state) => {
                state.leaveRequestsSearchInprogress = true;
            })
            .addCase(fetchLeaveRequestsAsync.rejected, (state) => {
                state.leaveRequestsSearchInprogress = false;
            })
            .addCase(fetchMyLeaveRequestsAsync.fulfilled, (state, action) => {
                state.myLeaveRequests = action.payload;
            })
            .addCase(fetchMyLeaveApprovalsAsync.fulfilled, (state, action) => {
                state.myLeaveApprovals = action.payload;
            });
    },
});

export const {
    leaveFilterChange,
    leaveRequestsFilterChange,
    myLeaveApprovalsFilterChange,
    myLeaveRequestsFilterChange,
} = leaveSlice.actions;

export const leaveTypesListSelector = (state: RootState) => state.leave.leaveTypes;
export const employeeLeavesSelector = (state: RootState) => state.leave.employeeLeaves;
export const employeeLeavesFilterSelector = (state: RootState) => state.leave.employeeLeavesFilter;
export const leaveTypesSelectListSelector = (state: RootState) => state.leave.leaveTypesSelectList;
export const leaveRequestsSelector = (state: RootState) => state.leave.leaveRequests;
export const leaveRequestsFilterSelector = (state: RootState) => state.leave.leaveRequestsFilter;
export const myLeaveRequestsSelector = (state: RootState) => state.leave.myLeaveRequests;
export const myLeaveRequestsFilterSelector = (state: RootState) => state.leave.myLeaveRequestsFilter;
export const myLeaveApprovalsSelector = (state: RootState) => state.leave.myLeaveApprovals;
export const myLeaveApprovalsFilterSelector = (state: RootState) => state.leave.myLeaveApprovalsFilter;

export default leaveSlice.reducer;
